import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {Chip, createStyles, Group, Text} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface SelectableCardProps {
  value: string;
  title: string;
  icon?: JSX.Element;
  subtitle?: string;
  description?: string;
  disabled?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

const baseCheckIconStyles = {
  alignSelf: 'start',
  height: '16px !important', // fighting with .svg-inline--fa
  width: 16,
  marginRight: 8,
};

const useStyles = createStyles((theme) => ({
  label: {
    height: 'unset', // default hardcoded to 1.75rem by Mantine
    fontWeight: 'unset', // fighting with benchling.less
    padding: 12,
    '&[data-checked]': {
      padding: 12, // default is to have smaller side padding when checked
      backgroundColor: theme.colors.blue[0],
      borderColor: theme.colors.blue[7],
      '&:hover': {
        backgroundColor: theme.colors.blue[1],
      },
    },
  },
  icon: {
    marginRight: 12,
    alignSelf: 'start',
  },
  builtInIcon: {
    display: 'none', // can't stop it from rendering, have to hide it
  },
  uncheckedIcon: {
    ...baseCheckIconStyles,
    color: theme.colors.gray[3],
    '[data-checked] &': {
      display: 'none !important', // fontawesome overrides this otherwise
    },
  },
  checkedIcon: {
    ...baseCheckIconStyles,
    display: 'none !important', // fontawesome overrides this otherwise
    color: theme.primaryColor,
    '[data-checked] &': {
      display: 'initial !important', // fontawesome overrides this otherwise
    },
  },
  textWrap: {
    // Allow text to wrap
    whiteSpace: 'normal',
  },
}));

/**
 * A card component that can be selected, typically used within a group of selectable cards.
 * To use, wrap multiple in a Mantine `Chip.Group` component.
 * @see https://v6.mantine.dev/core/chip/#controlled-chipgroup
 */
export default function SelectableCard({
  value,
  title,
  icon,
  subtitle,
  description,
  disabled,
  dataTestAttrs = {component: 'SelectableCard', element: 'root'},
}: SelectableCardProps): JSX.Element {
  const {classes} = useStyles();

  return (
    <Chip
      value={value}
      radius={5}
      classNames={{label: classes.label, iconWrapper: classes.builtInIcon}}
      disabled={disabled}
      wrapperProps={getTestProps(dataTestAttrs)}
      {...getTestProps({component: 'SelectableCard', element: 'label'})}
    >
      <Group spacing={0} noWrap={true}>
        {/**
         * Why render both check icons all the time? Because we can only toggle the visibility. This is
         * because we're not controlling the value of this component directly, so we just pay attention to the
         * data-checked attribute to decide which is visible via CSS.
         */}
        <BenchlingIcon icon="circle-outline" className={classes.uncheckedIcon} />
        <BenchlingIcon icon="check-circle" className={classes.checkedIcon} />
        {icon && <div className={classes.icon}>{icon}</div>}
        <div className={classes.textWrap}>
          <Text size="lg" weight={600} {...getTestProps({component: 'SelectableCard', element: 'title'})}>
            {title}
          </Text>
          {subtitle && (
            <Text
              color="dimmed"
              italic={true}
              {...getTestProps({component: 'SelectableCard', element: 'subtitle'})}
            >
              {subtitle}
            </Text>
          )}
          {description && (
            <Text {...getTestProps({component: 'SelectableCard', element: 'description'})}>
              {description}
            </Text>
          )}
        </div>
      </Group>
    </Chip>
  );
}
